import React from 'react';

import { Alert, Button, Card, Flex, message, Popconfirm, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { EpdSigningRequestSchema } from '@app/models/orders';
import { formatDateAndTime, fromUtc } from '@app/services/date';
import { useResetEpdRequest } from '@app/services/hooks/useResetEpdRequest';
import { RAW_INTERPOLATION } from '@app/services/i18n';
import { EpdError } from '@components/EpdError';
import { EpdIssues } from '@components/EpdIssues';

interface IProps {
  request: EpdSigningRequestSchema;
  onSign: () => void;
}

export const SigningRequest: React.FC<IProps> = ({ request, onSign }) => {
  const epdT = useTranslation(NSKeys.epd);
  const commonT = useTranslation(NSKeys.common);
  const reset = useResetEpdRequest();
  return (
    <Card
      size="small"
      title={epdT.t(`titles.${request.title}` as const)}
      extra={<Tag>{epdT.t(`requestStatuses.${request.status}` as const)}</Tag>}
    >
      <Flex vertical gap={12}>
        {request.blocked_reason === 'data' && <EpdIssues issues={request.issues} />}
        {request.blocked_reason === 'manual' && (
          <Alert
            type="error"
            showIcon
            title={epdT.t('failures', { count: request.failures })}
            description={request.last_error}
          />
        )}
        {request.blocked_reason !== 'manual' && request.last_error && <Alert type="error" title={request.last_error} />}
        {request.next_attempt_at && (
          <Typography.Text type="secondary">
            {epdT.t('nextAttempt', { date: formatDateAndTime(fromUtc(request.next_attempt_at)), ...RAW_INTERPOLATION })}
          </Typography.Text>
        )}
        {request.completed_at && (
          <Typography.Text>
            {formatDateAndTime(fromUtc(request.completed_at))}
            {request.completed_by && ['user', 'worker', 'operator'].includes(request.completed_by)
              ? ` · ${epdT.t(`completedBy.${request.completed_by as 'user' | 'worker' | 'operator'}` as const)}`
              : ''}
          </Typography.Text>
        )}
        <Space>
          {request.can_sign && (
            <Button type="primary" onClick={onSign}>
              {epdT.t('sign')}
            </Button>
          )}
          {request.can_reset && (
            <Popconfirm
              title={epdT.t('resetConfirm')}
              okText={commonT.t('confirm')}
              cancelText={commonT.t('cancel')}
              onConfirm={() =>
                reset.mutate(request.uid, {
                  onSuccess: () => {
                    message.success(commonT.t('dataPatchSuccess'));
                  },
                })
              }
            >
              <Button loading={reset.isLoading}>{commonT.t('buttonReset')}</Button>
            </Popconfirm>
          )}
        </Space>
        {reset.isError && <EpdError error={reset.error} />}
      </Flex>
    </Card>
  );
};
